import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Stack, TextField, Typography,
} from '@mui/material';
import {
  ExpandMoreOutlined as ExpandIcon,
  PhoneInTalkOutlined as CallIcon,
} from '@mui/icons-material';

import HiringService, { InterviewGuide } from '../../api/services/hiring.service';

/**
 * The interview script, editable.
 *
 * Plain text with three characters of convention rather than a builder
 * with sections and question types. The people who use this rewrite it
 * constantly — they are the ones who find out what lands on a call — and
 * a form would make every wording change somebody else's job.
 *
 * Reset deletes the override rather than restoring a copy, same as the
 * messages: a company that has never rewritten it keeps getting ours.
 */

const InterviewGuideEditor: React.FC<{
  guide: InterviewGuide;
  placeholders: string[];
  onChanged: (guide: InterviewGuide) => void;
}> = ({ guide, placeholders, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(guide.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) { setDraft(guide.body); setError(null); setSaved(false); }
  };

  const save = async (body: string) => {
    setBusy(true);
    setError(null);
    try {
      const result = await HiringService.saveGuide(body);
      onChanged(result.interviewGuide);
      setDraft(result.interviewGuide.body);
      setSaved(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.response?.data?.message || 'That script could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
        <CallIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        <Typography variant="subtitle2">Interview script</Typography>
        {!guide.isDefault && (
          <Chip size="small" variant="outlined" label="Edited" sx={{ height: 20, fontSize: 11 }} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the Interview button reads out on a candidate&apos;s page, with their name
        and the pay above already filled in.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          onClick={toggle}
          sx={{
            px: 2, py: 1.25, cursor: 'pointer',
            bgcolor: open ? 'action.hover' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
            {open ? 'Editing the script' : 'Read or edit the script'}
          </Typography>
          {saved && <Typography variant="caption" color="text.secondary">Saved</Typography>}
          <ExpandIcon
            fontSize="small"
            sx={{
              color: 'text.disabled',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          />
        </Stack>

        {open && (
          <Box sx={{ px: 2, pb: 2, pt: 1 }}>
            <TextField
              multiline
              fullWidth
              minRows={16}
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setSaved(false); }}
              inputProps={{ 'aria-label': 'Interview script' }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: 13, lineHeight: 1.7, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                },
              }}
            />

            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mt: 1.5 }}>
              {guide.syntax.map((rule) => (
                <Typography key={rule.prefix} variant="caption" color="text.secondary">
                  <Box component="code" sx={{ fontWeight: 700, mr: 0.5 }}>{rule.prefix}</Box>
                  {rule.meaning}
                </Typography>
              ))}
            </Stack>

            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Fills itself in:
              </Typography>
              {placeholders.map((name) => (
                <Chip
                  key={name}
                  size="small"
                  variant="outlined"
                  label={`{{${name}}}`}
                  onClick={() => { setDraft((d) => `${d}{{${name}}}`); setSaved(false); }}
                  sx={{ height: 20, fontSize: 11, fontFamily: 'monospace' }}
                />
              ))}
            </Stack>

            <Stack direction="row" gap={1} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                disableElevation
                disabled={busy || !draft.trim() || draft === guide.body}
                onClick={() => save(draft)}
              >
                Save
              </Button>
              {!guide.isDefault && (
                <Button size="small" color="inherit" disabled={busy} onClick={() => save('')}>
                  Reset to the original
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InterviewGuideEditor;
